import React, { useState, useEffect } from 'react';
import { Play, Star, ExternalLink, ChevronLeft, ChevronRight, Film } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WebSeriesCarousel = () => {
  const [webSeries, setWebSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 3;

  useEffect(() => {
    fetchWebSeries();
  }, []);

  const fetchWebSeries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/webseries/`);
      setWebSeries(response.data.series || []);
    } catch (error) {
      console.error('Error fetching web series:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(webSeries.length / itemsPerPage);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const visibleSeries = webSeries.slice(
    currentIndex * itemsPerPage,
    currentIndex * itemsPerPage + itemsPerPage
  );

  if (loading) {
    return (
      <section id="webseries" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Top <span className="text-[#0077cc]">OTT Web Series</span>
            </h2>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                  <div className="w-full h-64 bg-gray-200" />
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-full mb-4" />
                    <div className="h-10 bg-gray-200 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="webseries" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
            <Film size={16} />
            Original Content
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Top <span className="text-[#0077cc]">OTT Web Series</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Award-winning original content that captivates millions
          </p>
        </div>

        <div className="max-w-6xl mx-auto relative">
          {/* Navigation Buttons */}
          {totalPages > 1 && (
            <>
              <button
                onClick={handlePrev}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-all hover:scale-110"
                aria-label="Previous"
                data-testid="webseries-prev-btn"
              >
                <ChevronLeft size={24} className="text-gray-700" />
              </button>
              
              <button
                onClick={handleNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-all hover:scale-110"
                aria-label="Next"
                data-testid="webseries-next-btn"
              >
                <ChevronRight size={24} className="text-gray-700" />
              </button>
            </>
          )}

          {/* Carousel Content */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleSeries.map((series, index) => (
              <div 
                key={series.id || index} 
                className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300"
                data-testid={`webseries-card-${index}`}
              >
                {/* Thumbnail */}
                <div className="relative overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900">
                  <img
                    src={series.thumbnail}
                    alt={series.title}
                    className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                    onError={(e) => {
                      // Fallback to a gradient background with title
                      e.target.style.display = 'none';
                      e.target.parentElement.classList.add('min-h-[16rem]', 'flex', 'items-center', 'justify-center');
                      e.target.parentElement.innerHTML = `
                        <div class="text-center p-4">
                          <div class="text-4xl mb-2">🎬</div>
                          <div class="text-white font-bold">${series.title}</div>
                        </div>
                      `;
                    }}
                  />
                  {/* Play Overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <Play className="text-[#0077cc] ml-1" size={28} fill="currentColor" />
                    </div>
                  </div>
                  {/* Rating Badge */}
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1 shadow-lg">
                    <Star className="text-yellow-500" size={14} fill="currentColor" />
                    <span className="text-sm font-bold">{series.rating}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-1">
                    {series.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {series.description}
                  </p>
                  
                  {/* Stats */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-xs text-gray-500">Episodes</p>
                      <p className="text-lg font-bold text-[#0077cc]">{series.episodes}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Views</p>
                      <p className="text-lg font-bold text-[#0077cc]">{series.views}</p>
                    </div>
                  </div>

                  {/* CTA */}
                  <a
                    href={series.link || '#'}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-[#0077cc] to-[#00c6d7] text-white font-medium rounded-full hover:shadow-lg transition-shadow"
                    data-testid={`webseries-watch-btn-${index}`}
                  >
                    Watch Now
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Dots */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentIndex ? 'w-8 bg-[#0077cc]' : 'w-2 bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to page ${index + 1}`}
                  data-testid={`webseries-dot-${index}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WebSeriesCarousel;
